import { useQuery } from '@tanstack/react-query'
import { Pencil, Plus, UserCog } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useGuardians } from '@/features/auth/guardians'
import { EmptyState } from '@/components/feedback/States'
import { DataTable, type Column } from '@/components/tables/DataTable'
import { FilterBar, SearchInput } from '@/components/tables/FilterBar'
import { Pagination } from '@/components/tables/Pagination'
import { Avatar } from '@/components/ui/Avatar'
import { Badge, type BadgeProps } from '@/components/ui/Badge'
import { Button, IconButton } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { PageHeader } from '@/components/ui/PageHeader'
import { Select } from '@/components/ui/Select'
import type { Role } from '@/features/auth/types'
import { useAuth } from '@/features/auth/useAuth'
import { rolesQuery, usersQuery, type ManagedUser } from '@/features/users/api'
import { UserDialog } from '@/features/users/components/UserDialog'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { useListParams } from '@/hooks/useListParams'
import { formatDateTime, humanize } from '@/utils/format'

const FILTERS = ['role', 'active'] as const

const roleTone: Record<Role, NonNullable<BadgeProps['tone']>> = {
  super_admin: 'danger',
  admin: 'brand',
  teacher: 'info',
  accountant: 'warning',
  librarian: 'success',
  receptionist: 'neutral',
  student: 'neutral',
  parent: 'neutral',
}

function StatusBadge({ user }: { user: ManagedUser }) {
  const { t } = useTranslation('settings')
  return (
    <Badge tone={user.isActive ? 'success' : 'neutral'} dot size="sm">
      {user.isActive ? t('Active') : t('Inactive')}
    </Badge>
  )
}

export function UsersPage() {
  useDocumentTitle('Users')
  const { t } = useTranslation('settings')
  const { user: me } = useAuth()
  const { state, query, update, toggleSort, setPage } = useListParams(FILTERS, { sort: 'name' })
  const list = useQuery(usersQuery(query))
  const roles = useQuery(rolesQuery())
  const withGuardians = useGuardians()
  const [editing, setEditing] = useState<ManagedUser | 'new' | null>(null)

  const columns: Column<ManagedUser>[] = [
    {
      key: 'name',
      header: t('User'),
      sortKey: 'name',
      cell: (u) => (
        <div className="flex items-center gap-3">
          <Avatar name={u.name} size="sm" />
          <div className="min-w-0">
            <p className="truncate font-medium text-ink">
              {u.name}
              {u.id === me?.id ? (
                <span className="ml-1.5 text-xs font-normal text-muted">{t('(you)')}</span>
              ) : null}
            </p>
            <p className="truncate text-xs text-muted">{u.email}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'role',
      header: t('Role'),
      sortKey: 'role',
      cell: (u) => (
        <Badge tone={roleTone[u.role]} size="sm">
          {t(u.roleLabel)}
        </Badge>
      ),
    },
    {
      key: 'portals',
      header: t('Portals'),
      hideBelow: 'lg',
      cell: (u) => (
        <span className="flex flex-wrap gap-1">
          {u.portals.map((p) => (
            <Badge key={p} size="sm">
              {humanize(p)}
            </Badge>
          ))}
        </span>
      ),
    },
    {
      key: 'lastLogin',
      header: t('Last sign-in'),
      sortKey: 'lastLoginAt',
      hideBelow: 'xl',
      cell: (u) =>
        u.lastLoginAt ? formatDateTime(u.lastLoginAt) : <span className="text-subtle">{t('Never')}</span>,
    },
    { key: 'status', header: t('Status'), cell: (u) => <StatusBadge user={u} /> },
    {
      key: 'actions',
      header: <span className="sr-only">{t('Actions')}</span>,
      align: 'right',
      cell: (u) => (
        <IconButton
          label={t('Edit {{name}}', { name: u.name })}
          icon={<Pencil />}
          size="icon-sm"
          onClick={(e) => {
            e.stopPropagation()
            setEditing(u)
          }}
        />
      ),
    },
  ]

  const hasFilters = Boolean(state.search || Object.keys(state.filters).length)

  return (
    <>
      <PageHeader
        title={t('Users')}
        description={t('Everyone who can sign in, their role and which portals they can use.')}
        breadcrumbs={[{ label: t('Settings') }, { label: t('Users') }]}
        homeHref="/admin"
        actions={
          <Button leftIcon={<Plus />} onClick={() => setEditing('new')}>
            {t('New user')}
          </Button>
        }
      />
      <Card>
        <FilterBar
          actions={
            hasFilters ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => update({ search: undefined, role: undefined, active: undefined })}
              >
                {t('Clear filters')}
              </Button>
            ) : null
          }
        >
          <SearchInput
            value={state.search}
            onChange={(v) => update({ search: v })}
            placeholder={t('Name or email')}
            label={t('Search users')}
          />
          <Select
            aria-label={t('Filter by role')}
            className="sm:w-auto sm:min-w-44"
            placeholder={t('All roles')}
            value={state.filters.role ?? ''}
            onChange={(e) => update({ role: e.target.value })}
            options={(roles.data ?? [])
              .filter((r) => withGuardians || r.value !== 'parent')
              .map((r) => ({ value: r.value, label: t(r.label) }))}
          />
          <Select
            aria-label={t('Filter by status')}
            className="sm:w-auto sm:min-w-36"
            placeholder={t('Any status')}
            value={state.filters.active ?? ''}
            onChange={(e) => update({ active: e.target.value })}
            options={[
              { value: '1', label: t('Active') },
              { value: '0', label: t('Inactive') },
            ]}
          />
        </FilterBar>
        <DataTable
          caption={t('Users')}
          columns={columns}
          rows={list.data?.data}
          getRowId={(u) => u.id}
          loading={list.isPending}
          error={list.error}
          onRetry={() => void list.refetch()}
          sort={{ key: state.sort, direction: state.direction }}
          onSortChange={toggleSort}
          onRowClick={(u) => setEditing(u)}
          mobileCard={(u) => (
            <div className="flex items-center gap-3">
              <Avatar name={u.name} />
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium text-ink">{u.name}</p>
                <p className="truncate text-xs text-muted">{u.email}</p>
                <p className="mt-1 text-xs text-muted">
                  {t(u.roleLabel)} ·{' '}
                  {u.lastLoginAt
                    ? t('last seen {{date}}', { date: formatDateTime(u.lastLoginAt) })
                    : t('never signed in')}
                </p>
              </div>
              <StatusBadge user={u} />
            </div>
          )}
          empty={
            <EmptyState
              icon={<UserCog />}
              title={hasFilters ? t('No users match these filters') : t('No users yet')}
              description={
                hasFilters
                  ? t('Try a different search or clear the filters.')
                  : t('Create accounts for the people who run the school.')
              }
              compact
            />
          }
        />
        <Pagination
          meta={list.data?.meta}
          onPageChange={setPage}
          onPerPageChange={(n) => update({ perPage: n })}
        />
      </Card>

      <UserDialog
        open={editing !== null}
        onOpenChange={(open) => (open ? undefined : setEditing(null))}
        user={editing === 'new' ? null : editing}
      />
    </>
  )
}
